export const GameStatus = {
  RUNNING: "RUNNING",
  PAUSED: "PAUSED",
  GAME_OVER: "GAME_OVER",
} as const;

export type GameStatus = (typeof GameStatus)[keyof typeof GameStatus];

type GameStateEvents = {
  changed: (next: GameStatus, prev: GameStatus) => void;
  exitHome: () => void;
};

export class GameStateController {
  private emitter = new Phaser.Events.EventEmitter();
  private _status: GameStatus = GameStatus.RUNNING;

  get status() {
    return this._status;
  }

  get isPuased() {
    return this._status === GameStatus.PAUSED;
  }

  get isGameOver() {
    return this._status === GameStatus.GAME_OVER;
  }

  on<K extends keyof GameStateEvents>(events: K, cb: GameStateEvents[K]) {
    this.emitter.on(events, cb);
    return () => {
      this.emitter.off(events, cb);
    };
  }

  private setStatus(next: GameStatus) {
    if (this._status === next) return;
    const prev = this._status;
    this._status = next;
    this.emitter.emit("changed", next, prev);
  }

  start() {
    this.setStatus(GameStatus.RUNNING);
  }

  pause() {
    if (this.isGameOver) return;
    this.setStatus(GameStatus.PAUSED);
  }

  resume() {
    if (this.isGameOver) return;
    this.setStatus(GameStatus.RUNNING);
  }

  togglePause() {
    if (this.isGameOver) return;
    if (this.isPuased) this.resume();
    else this.pause();
  }

  gameOver() {
    this.setStatus(GameStatus.GAME_OVER);
  }

  destroy() {
    this.emitter.removeAllListeners();
  }

  emitExitHome() {
    if (this.isGameOver) return;
    this.emitter.emit("exitHome");
  }

  onExitHome(cb: () => void) {
    this.emitter.on("exitHome", cb);
    return () => {
      this.emitter.off("exitHome", cb);
    };
  }
}
